import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  resolveNotificationText,
  type NotificationText,
  type NotificationTextSource,
  type NotificationTranslationKey,
} from "../utils/notifications/notification-text";

/**
 * Ц6/AD-378. Мост между экраном уведомлений и разбором ключей перевода.
 *
 * Отдельный хук нужен по двум причинам. Во-первых, разбор `data` — чистая
 * функция, и пробе незачем поднимать i18next ради неё. Во-вторых, `t` из
 * react-i18next перегружен по словарю каждого пространства имён, и передача
 * его самого в функцию со свободным строковым ключом роняет компилятор
 * («Debug Failure. No error for last overload signature» — та же мина, из-за
 * которой `common` в `src/types/i18next.d.ts` описан как `Record<string,
 * string>`). Стрелка с явным типом параметров эту перегрузку не разрешает.
 */
export function useNotificationText(): (
  notification: NotificationTextSource,
) => NotificationText {
  const { t } = useTranslation("notifications");

  const translate = useCallback(
    (
      key: NotificationTranslationKey,
      options?: Record<string, unknown>,
    ): string => {
      const value = t(key, options ?? {});
      return typeof value === "string" ? value : key;
    },
    [t],
  );

  return useCallback(
    (notification: NotificationTextSource): NotificationText =>
      resolveNotificationText(notification, translate),
    [translate],
  );
}
